import { Link } from "@tanstack/react-router";
import { ImageIcon, Plus } from "@/components/icons";
import {
  Badge,
  Button,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Skeleton,
} from "@/components/kit";
import type { ProductListItemDTO } from "@/modules/products/domain/types";
import { formatLakRange } from "@/shared/lib/currency";
import { AppImage } from "@/shared/ui/AppImage";

export function ProductGrid({
  products,
  isLoading,
  canManage,
  onCreate,
}: {
  products: ProductListItemDTO[];
  isLoading?: boolean;
  canManage?: boolean;
  onCreate?: () => void;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-2xl" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="brand-card p-8 sm:p-12">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ImageIcon />
            </EmptyMedia>
            <EmptyTitle>ຍັງບໍ່ມີສິນຄ້າ</EmptyTitle>
            <EmptyDescription>
              ເພີ່ມສິນຄ້າເພື່ອໃຊ້ເປັນຄລັງອ້າງອີງໃນແຊັດ ຫຼື ປັບຕົວກັ່ນຕອງ
            </EmptyDescription>
          </EmptyHeader>
          {canManage && onCreate ? (
            <EmptyContent>
              <Button className="rounded-full" onClick={onCreate}>
                <Plus data-icon="inline-start" />
                ເພີ່ມສິນຄ້າແລກກ
              </Button>
            </EmptyContent>
          ) : null}
        </Empty>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
      {products.map((product) => (
        <Link
          key={product.id}
          to="/app/products/$id/edit"
          params={{ id: product.id }}
          className="brand-card group flex flex-col overflow-hidden py-0 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-secondary/15"
        >
          <div className="relative aspect-square overflow-hidden bg-muted/30">
            {product.imageKey ? (
              <AppImage
                src={product.imageKey}
                alt={product.name}
                className="absolute inset-0 size-full"
                fit="cover"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-muted-foreground">
                <ImageIcon className="size-14 opacity-30" />
              </div>
            )}

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/50 to-transparent px-3 pt-10 pb-3">
              <div className="flex flex-wrap gap-1">
                {product.categories.slice(0, 2).map((category) => (
                  <Badge
                    key={category.id}
                    variant="outline"
                    className="border-0 bg-background/95 text-foreground text-xs shadow-md backdrop-blur-sm"
                  >
                    {category.name}
                  </Badge>
                ))}
                {product.categories.length > 2 ? (
                  <Badge
                    variant="outline"
                    className="border-0 bg-background/95 text-foreground text-xs shadow-md backdrop-blur-sm"
                  >
                    +{product.categories.length - 2}
                  </Badge>
                ) : null}
              </div>
            </div>

            {product.isLowStock ? (
              <Badge
                variant="destructive"
                className="absolute top-2.5 right-2.5 shadow-sm"
              >
                ໃກ້ໝົດ
              </Badge>
            ) : product.totalStock > 0 ? (
              <Badge
                variant="secondary"
                className="absolute top-2.5 right-2.5 border-0 bg-leaf text-leaf-foreground shadow-sm"
              >
                ຄົງເຫຼືອ {product.totalStock}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="absolute top-2.5 right-2.5 bg-card/90 shadow-sm backdrop-blur-sm"
              >
                ຫມົດ
              </Badge>
            )}
          </div>

          <div className="flex flex-col gap-2 p-4">
            <span className="font-medium leading-snug group-hover:text-secondary">
              {product.name}
            </span>
            <div className="flex items-end justify-between gap-2 text-sm">
              <span className="text-muted-foreground text-xs">
                {product.variantCount} ຕົວເລືອກ
              </span>
              <span className="font-semibold text-secondary tabular-nums">
                {formatLakRange(product.minPrice, product.maxPrice)}
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}
